import { useCallback } from 'react'

import { Flex, HStack } from 'govwell-ui'
import { DateTemplateInput, LateFeeConfigurationInput } from 'types/graphql'

import DateTemplateInputs from 'src/components/DateTemplateInputs/DateTemplateInputs'
import FeeTemplateSelect from 'src/components/Selects/FeeTemplateSelect'
import { DateTemplate } from 'src/models/Settings/DateTemplate'
import { LateFeeTypes } from 'src/utils/payments'

import { TanstackField } from '../../../utils/forms'

const DEFAULT_DATE_TEMPLATE = {}

type Props<TKey extends string> = {
  field: TanstackField<LateFeeConfigurationInput | null | undefined, TKey>
  isDisabled?: boolean
  onValueChange?: (value: LateFeeConfigurationInput | null | undefined) => void
}
const LateFeeConfigurationField = <TKey extends string>({
  field,
  isDisabled,
  onValueChange,
}: Props<TKey>) => {
  const handleValueChange = useCallback(
    (value: LateFeeConfigurationInput | null | undefined) => {
      field.setValue(value)
      onValueChange?.(value)
    },
    [field, onValueChange]
  )

  const handleFeeTemplateChange = useCallback(
    (value: number | null | undefined) => {
      handleValueChange({
        ...field.state.value,
        feeTemplateId: value,
      })
    },
    [field.state.value, handleValueChange]
  )

  const handleAccrualLimitDateTemplateChange = useCallback(
    (value: Partial<DateTemplateInput> | null | undefined) => {
      handleValueChange({
        ...field.state.value,
        accrualLimitDateTemplateInput: value ? DateTemplate.fromInput(value).protocol : null,
      })
    },
    [field.state.value, handleValueChange]
  )

  return (
    <HStack gap={4} wrap="wrap">
      <Flex flex={1}>
        <FeeTemplateSelect
          allowedTypes={LateFeeTypes}
          caption="The base late fee template to apply when an invoice is overdue"
          isDisabled={isDisabled}
          isMultiSelect={false}
          label="Fee Template"
          onValueChange={handleFeeTemplateChange}
          value={field.state.value?.feeTemplateId ?? null}
        />
      </Flex>
      <Flex flex={1}>
        <DateTemplateInputs
          caption={`The date after which the late fee will stop accruing ${
            field.state.value?.accrualLimitDateTemplateInput?.type === 'Interval'
              ? 'relative to the invoice due date '
              : ''
          }(optional)`}
          dateTemplate={field.state.value?.accrualLimitDateTemplateInput ?? DEFAULT_DATE_TEMPLATE}
          isDisabled={isDisabled}
          label="Accrual End Date"
          onChange={handleAccrualLimitDateTemplateChange}
        />
      </Flex>
    </HStack>
  )
}

export default LateFeeConfigurationField
